import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GurujiChatWidget from '../components/GurujiChatWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar, BookOpen, Award, TrendingUp, Users, FileText, 
  CheckCircle, Clock, DollarSign, MapPin, Download, ArrowRight, 
  AlertCircle, ChevronDown, ChevronUp, BarChart3, PieChart, Crosshair
} from 'lucide-react';
import { getExamById } from '../data/examData';
import {
  upsertMetaByName,
  upsertMetaByProperty,
  upsertCanonical,
  upsertJsonLd
} from '../lib/seoHead';

// (SEO helpers live in /lib/seoHead.js — shared with BlogDetail, etc.)

const ExamDetails = () => {
  const { examId } = useParams();
  const [exam, setExam] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const examData = getExamById(examId);
    if (examData) {
      setExam(examData);
      window.scrollTo(0, 0);
    }
  }, [examId]);

  // Inject dynamic SEO tags (title, description, canonical, OG, JSON-LD) on every navigation
  useEffect(() => {
    if (!exam) return;

    const title = exam.metaTitle || `${exam.name} 2026: Exam, Syllabus, Preparation | Vidya365`;
    const description = exam.metaDescription || `Complete guide to ${exam.fullName || exam.name} — notification, eligibility, pattern, syllabus, cut-off and preparation at Vidya365.`;
    const canonical = `https://vidya365.in/exams/${exam.id}`;
    const keywords = `${exam.name} exam, ${exam.name} syllabus, ${exam.name} coaching, ${exam.name} preparation, ${exam.fullName}, ${exam.name} Karol Bagh Delhi, Vidya365`;

    document.title = title;
    upsertMetaByName('description', description);
    upsertMetaByName('keywords', keywords);
    upsertCanonical(canonical);
    upsertMetaByProperty('og:title', title);
    upsertMetaByProperty('og:description', description);
    upsertMetaByProperty('og:url', canonical);
    upsertMetaByProperty('og:type', 'website');
    upsertMetaByName('twitter:title', title);
    upsertMetaByName('twitter:description', description);

    upsertJsonLd('exam-course', {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: `${exam.name} Coaching`,
      alternateName: exam.fullName,
      description,
      url: canonical,
      provider: {
        '@type': 'EducationalOrganization',
        name: 'Vidya365',
        sameAs: 'https://vidya365.in'
      },
      courseMode: ['online', 'onsite'],
      educationalCredentialAwarded: exam.fullName,
      inLanguage: 'en',
      offers: {
        '@type': 'Offer',
        category: 'Competitive Exam Coaching',
        priceCurrency: 'INR',
        availability: 'https://schema.org/InStock'
      },
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'Blended',
        courseWorkload: 'PT6M'
      }
    });

    // FAQPage schema — unlocks Google's FAQ rich result (expandable Q&A in SERPs)
    const faqEntries = [
      {
        question: `What is the eligibility for ${exam.name}?`,
        answer: exam.eligibility?.education || 'Check the eligibility section for detailed requirements.'
      },
      {
        question: `How many times can I attempt ${exam.name}?`,
        answer: exam.eligibility?.attempts || 'Check the eligibility section for attempt details.'
      },
      {
        question: `What is the exam pattern for ${exam.name}?`,
        answer: `The ${exam.name} exam consists of ${exam.examPattern?.phases?.length || 'multiple'} phases. Check the exam pattern section for complete details.`
      },
      {
        question: `What is the salary for ${exam.name}?`,
        answer: exam.salary?.totalCTC
          ? `The total CTC for ${exam.name} is ${exam.salary.totalCTC} per annum.`
          : 'Check the salary section for complete details.'
      },
      {
        question: `When is the ${exam.name} notification expected?`,
        answer: exam.notification?.expected
          ? `The ${exam.name} notification is expected in ${exam.notification.expected}.`
          : `Visit Vidya365 for the latest ${exam.name} notification updates.`
      },
      {
        question: `What is the best coaching for ${exam.name}?`,
        answer: `Vidya365 offers the best ${exam.name} coaching in Karol Bagh, Delhi with expert faculty, comprehensive study material, mock tests, and a proven track record. Both online and offline classes are available.`
      }
    ];

    upsertJsonLd('exam-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqEntries.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.answer
        }
      }))
    });

    // Breadcrumb schema — helps Google render breadcrumb trail instead of raw URL
    upsertJsonLd('exam-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://vidya365.in/' },
        { '@type': 'ListItem', position: 2, name: 'Exams', item: 'https://vidya365.in/exams' },
        { '@type': 'ListItem', position: 3, name: exam.name, item: canonical }
      ]
    });
  }, [exam]);

  if (!exam) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <AlertCircle className="w-16 h-16 mx-auto text-red-600 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Exam Not Found</h2>
          <p className="text-gray-600 mb-6">The exam you're looking for doesn't exist.</p>
          <Link to="/exams">
            <Button className="bg-red-600 hover:bg-red-700 text-white">
              View All Exams
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Cut-off Trend Visualization
  const CutoffTrendChart = ({ data, title }) => {
    if (!data) return null;
    const years = Object.keys(data);
    const values = Object.values(data);
    const maxValue = Math.max(...values);

    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-red-600" />
          {title}
        </h3>
        <div className="space-y-3">
          {years.map((year) => {
            const percentage = (data[year] / maxValue) * 100;
            return (
              <div key={year}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-gray-700">{year}</span>
                  <span className="font-bold text-red-600">{data[year]} marks</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-red-500 to-red-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Exam Pattern Visual
  const ExamPatternVisual = ({ phase }) => {
    if (!phase.sections) return null;
    const totalMarks = phase.totalMarks;

    return (
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: Subject breakdown bars */}
          <div>
            <h4 className="font-bold text-gray-800 mb-4">Subject-wise Marks Distribution</h4>
            <div className="space-y-3">
              {phase.sections.map((section, idx) => {
                const percentage = (section.marks / totalMarks) * 100;
                const colors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500'];
                return (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-gray-700">{section.subject}</span>
                      <span className="font-bold text-gray-800">{section.marks} marks</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className={`${colors[idx % colors.length]} h-2.5 rounded-full`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Stats cards */}
          <div className="space-y-3">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="text-sm text-gray-600">Total Questions</div>
              <div className="text-3xl font-bold text-blue-600">{phase.totalQuestions}</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="text-sm text-gray-600">Total Marks</div>
              <div className="text-3xl font-bold text-green-600">{phase.totalMarks}</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="text-sm text-gray-600">Duration</div>
              <div className="text-3xl font-bold text-purple-600">{phase.totalTime} min</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // FAQ Component
  const FAQItem = ({ question, answer, index }) => {
    const isOpen = openFaq === index;
    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setOpenFaq(isOpen ? null : index)}
          className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
        >
          <span className="font-semibold text-left text-gray-800">{question}</span>
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-red-600 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
          )}
        </button>
        {isOpen && (
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <p className="text-gray-700">{answer}</p>
          </div>
        )}
      </div>
    );
  };

  const faqs = [
    {
      question: `What is the eligibility for ${exam.name}?`,
      answer: `${exam.eligibility?.education || 'Check the eligibility section above for detailed requirements.'}`
    },
    {
      question: `How many times can I attempt ${exam.name}?`,
      answer: exam.eligibility?.attempts || 'Check the eligibility section for attempt details.'
    },
    {
      question: `What is the exam pattern for ${exam.name}?`,
      answer: `The exam consists of ${exam.examPattern?.phases?.length || 'multiple'} phases. Check the exam pattern section for complete details.`
    },
    {
      question: `What is the salary for ${exam.name}?`,
      answer: exam.salary?.totalCTC 
        ? `The total CTC is ${exam.salary.totalCTC} per annum.` 
        : 'Check the salary section above for complete details.'
    }
  ];

  const TableOfContents = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-24 hidden lg:block">
      <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5 text-red-600" />
        Quick Navigation
      </h3>
      <nav className="space-y-2">
        {['Overview', 'Notification', 'Vacancy', 'Eligibility', 'Exam Pattern', 'Syllabus', 'Salary', 'Preparation', 'FAQs'].map((section) => (
          <button
            key={section}
            onClick={() => {
              const element = document.getElementById(section.toLowerCase());
              element?.scrollIntoView({ behavior: 'smooth' });
              setActiveSection(section.toLowerCase());
            }}
            className={`block w-full text-left px-4 py-2.5 rounded-lg transition-colors text-sm ${
              activeSection === section.toLowerCase()
                ? 'bg-red-600 text-white font-semibold'
                : 'text-gray-600 hover:bg-red-50 hover:text-red-600'
            }`}
          >
            {section}
          </button>
        ))}
      </nav>
      <div className="mt-6 pt-6 border-t border-gray-200">
        <Link to="/student/register">
          <Button className="w-full bg-red-600 hover:bg-red-700 text-white">
            Register for Test Series
          </Button>
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <GurujiChatWidget />

      {/* Hero Section - Improved */}
      <div className="bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white py-12 relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-48 translate-y-48"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-start gap-6 mb-6">
            <span className="text-7xl">{exam.icon}</span>
            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl font-bold mb-3">{exam.name} 2026</h1>
              <p className="text-xl opacity-90 mb-4">{exam.fullName}</p>
              <p className="text-lg opacity-80 max-w-3xl">{exam.overview?.intro}</p>
            </div>
          </div>
          
          {/* Quick Stats - Enhanced */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {exam.overview && (
              <>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <p className="text-sm opacity-80 mb-1">Conducted By</p>
                  <p className="font-bold text-lg">{exam.overview.conductedBy}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <p className="text-sm opacity-80 mb-1">Post Level</p>
                  <p className="font-bold text-lg">{exam.overview.level}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <p className="text-sm opacity-80 mb-1">Frequency</p>
                  <p className="font-bold text-lg">{exam.overview.frequency}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <p className="text-sm opacity-80 mb-1">Exam Mode</p>
                  <p className="font-bold text-lg">{exam.overview.mode || 'Online'}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Table of Contents (Desktop) */}
          <div className="lg:col-span-1">
            <TableOfContents />
          </div>

          {/* Content Sections */}
          <div className="lg:col-span-3 space-y-8">
            
            {/* Latest Update Banner */}
            {exam.notification && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500 p-5 rounded-lg shadow-sm">
                <div className="flex items-start gap-3">
                  <Calendar className="w-6 h-6 text-yellow-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-yellow-900 text-lg mb-1">Latest Update</p>
                    <p className="text-yellow-800">
                      {exam.name} 2026 notification expected in <strong className="text-yellow-900">{exam.notification.expected}</strong>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Overview Section - Enhanced */}
            <section id="overview" className="scroll-mt-24">
              <Card className="shadow-lg border-l-4 border-l-red-600">
                <CardHeader className="bg-gradient-to-r from-red-50 to-white">
                  <CardTitle className="flex items-center gap-3 text-2xl">
                    <div className="p-2 bg-red-600 rounded-lg">
                      <BookOpen className="w-6 h-6 text-white" />
                    </div>
                    What is {exam.name}?
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  <p className="text-gray-700 leading-relaxed text-lg">{exam.overview?.intro}</p>
                  {exam.overview && (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-5 border border-red-100">
                        <p className="text-sm text-gray-600 mb-1">Conducting Body</p>
                        <p className="font-bold text-gray-800 text-lg">{exam.overview.conductedBy}</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-5 border border-blue-100">
                        <p className="text-sm text-gray-600 mb-1">Post Level</p>
                        <p className="font-bold text-gray-800 text-lg">{exam.overview.level}</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-5 border border-green-100">
                        <p className="text-sm text-gray-600 mb-1">Exam Frequency</p>
                        <p className="font-bold text-gray-800 text-lg">{exam.overview.frequency}</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-100">
                        <p className="text-sm text-gray-600 mb-1">Exam Mode</p>
                        <p className="font-bold text-gray-800 text-lg">{exam.overview.mode}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </section>

            {/* Notification & Important Dates - Enhanced */}
            {exam.notification && (
              <section id="notification" className="scroll-mt-24">
                <Card className="shadow-lg border-l-4 border-l-blue-600">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-white">
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      <div className="p-2 bg-blue-600 rounded-lg">
                        <Calendar className="w-6 h-6 text-white" />
                      </div>
                      {exam.name} 2026 Notification & Important Dates
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                            <th className="px-6 py-4 text-left font-bold">Event</th>
                            <th className="px-6 py-4 text-left font-bold">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          <tr className="hover:bg-blue-50 transition-colors">
                            <td className="px-6 py-4 font-medium text-gray-800">Notification Release</td>
                            <td className="px-6 py-4 font-bold text-red-600">{exam.notification.expected}</td>
                          </tr>
                          <tr className="bg-gray-50 hover:bg-blue-50 transition-colors">
                            <td className="px-6 py-4 font-medium text-gray-800">Application Start Date</td>
                            <td className="px-6 py-4 text-gray-700">{exam.notification.applicationStart || 'With notification'}</td>
                          </tr>
                          <tr className="hover:bg-blue-50 transition-colors">
                            <td className="px-6 py-4 font-medium text-gray-800">Last Date to Apply</td>
                            <td className="px-6 py-4 text-gray-700">{exam.notification.lastDate || 'To be announced'}</td>
                          </tr>
                          {exam.notification.phase1Exam && (
                            <tr className="bg-gray-50 hover:bg-blue-50 transition-colors">
                              <td className="px-6 py-4 font-medium text-gray-800">Phase 1 / Prelims Exam</td>
                              <td className="px-6 py-4 text-gray-700">{exam.notification.phase1Exam}</td>
                            </tr>
                          )}
                          {exam.notification.phase2Exam && (
                            <tr className="hover:bg-blue-50 transition-colors">
                              <td className="px-6 py-4 font-medium text-gray-800">Phase 2 / Mains Exam</td>
                              <td className="px-6 py-4 text-gray-700">{exam.notification.phase2Exam}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {exam.notification.lastYear && (
                      <p className="text-sm text-gray-600 mt-4 bg-gray-50 p-3 rounded-lg">
                        <strong>Note:</strong> Last year's notification was released on {exam.notification.lastYear}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Vacancy Details - Enhanced */}
            {exam.vacancy && (
              <section id="vacancy" className="scroll-mt-24">
                <Card className="shadow-lg border-l-4 border-l-green-600">
                  <CardHeader className="bg-gradient-to-r from-green-50 to-white">
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      <div className="p-2 bg-green-600 rounded-lg">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                      {exam.name} Vacancy 2026
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="bg-gradient-to-br from-green-100 to-teal-100 rounded-xl p-8 mb-6 text-center border-2 border-green-300">
                      <p className="text-5xl font-bold text-green-700 mb-2">{exam.vacancy.expected}</p>
                      <p className="text-gray-700 font-medium">Expected vacancies for 2026</p>
                    </div>
                    {exam.vacancy.lastYear && (
                      <p className="text-gray-700 mb-4">
                        Previous year: <strong className="text-green-700">{exam.vacancy.lastYear}</strong>
                      </p>
                    )}
                    {exam.vacancy.posts && (
                      <div className="mt-6">
                        <p className="font-bold text-gray-800 mb-4 text-lg">Posts Available:</p>
                        <div className="grid md:grid-cols-2 gap-3">
                          {exam.vacancy.posts.map((post, idx) => (
                            <div key={idx} className="flex items-center gap-3 bg-green-50 p-3 rounded-lg border border-green-200">
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                              <span className="text-gray-700">{post}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Eligibility Criteria - Enhanced */}
            {exam.eligibility && (
              <section id="eligibility" className="scroll-mt-24">
                <Card className="shadow-lg border-l-4 border-l-purple-600">
                  <CardHeader className="bg-gradient-to-r from-purple-50 to-white">
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      <div className="p-2 bg-purple-600 rounded-lg">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                      {exam.name} Eligibility Criteria 2026
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-6">
                    {exam.eligibility.age && (
                      <div>
                        <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-purple-600" />
                          Age Limit
                        </h3>
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                          <div className="grid md:grid-cols-2 gap-4 mb-4">
                            <div className="text-center bg-white rounded-lg p-4 border-2 border-blue-300">
                              <span className="text-gray-600 block mb-1">Minimum Age</span>
                              <span className="font-bold text-3xl text-blue-700">{exam.eligibility.age.min} years</span>
                            </div>
                            <div className="text-center bg-white rounded-lg p-4 border-2 border-blue-300">
                              <span className="text-gray-600 block mb-1">Maximum Age</span>
                              <span className="font-bold text-3xl text-blue-700">{exam.eligibility.age.max} years</span>
                            </div>
                          </div>
                          {exam.eligibility.age.relaxation && (
                            <p className="text-sm text-gray-700 bg-white/70 p-3 rounded-lg">
                              <strong>Note:</strong> {exam.eligibility.age.relaxation}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    <div>
                      <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-purple-600" />
                        Educational Qualification
                      </h3>
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                        <p className="text-gray-700 leading-relaxed">{exam.eligibility.education}</p>
                      </div>
                    </div>

                    {exam.eligibility.attempts && (
                      <div>
                        <h3 className="font-bold text-lg text-gray-800 mb-4">Number of Attempts</h3>
                        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
                          <p className="text-gray-700">{exam.eligibility.attempts}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Exam Pattern with Visualization */}
            {exam.examPattern && (
              <section id="exam pattern" className="scroll-mt-24">
                <Card className="shadow-lg border-l-4 border-l-indigo-600">
                  <CardHeader className="bg-gradient-to-r from-indigo-50 to-white">
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      <div className="p-2 bg-indigo-600 rounded-lg">
                        <BarChart3 className="w-6 h-6 text-white" />
                      </div>
                      {exam.name} Exam Pattern 2026
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-8">
                    {exam.examPattern.phases?.map((phase, phaseIdx) => (
                      <div key={phaseIdx} className="space-y-4">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-600 to-orange-600 text-white flex items-center justify-center font-bold text-lg">
                            {phaseIdx + 1}
                          </div>
                          <div>
                            <h3 className="font-bold text-xl text-gray-800">{phase.name}</h3>
                            {phase.type && (
                              <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full mt-1 inline-block">
                                {phase.type}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Visual Pattern Chart */}
                        {phase.sections && <ExamPatternVisual phase={phase} />}

                        {/* Detailed Table */}
                        {phase.sections && (
                          <div className="overflow-x-auto rounded-lg border border-gray-200 mt-4">
                            <table className="w-full">
                              <thead>
                                <tr className="bg-gradient-to-r from-gray-800 to-gray-900 text-white">
                                  <th className="px-4 py-3 text-left font-bold">Subject</th>
                                  <th className="px-4 py-3 text-center font-bold">Questions</th>
                                  <th className="px-4 py-3 text-center font-bold">Marks</th>
                                  <th className="px-4 py-3 text-center font-bold">Time (min)</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {phase.sections.map((section, idx) => (
                                  <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                                    <td className="px-4 py-3 font-medium text-gray-800">{section.subject}</td>
                                    <td className="px-4 py-3 text-center text-gray-700">{section.questions}</td>
                                    <td className="px-4 py-3 text-center font-bold text-red-600">{section.marks}</td>
                                    <td className="px-4 py-3 text-center text-gray-700">{section.time}</td>
                                  </tr>
                                ))}
                                <tr className="bg-gradient-to-r from-red-100 to-orange-100 font-bold border-t-2 border-red-300">
                                  <td className="px-4 py-3">Total</td>
                                  <td className="px-4 py-3 text-center">{phase.totalQuestions}</td>
                                  <td className="px-4 py-3 text-center text-red-700 text-lg">{phase.totalMarks}</td>
                                  <td className="px-4 py-3 text-center">{phase.totalTime}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )}

                        {phase.negativeMarking && (
                          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                            <p className="text-sm text-yellow-800">
                              <strong>⚠️ Negative Marking:</strong> {phase.negativeMarking}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Cutoff Trends with Charts */}
            {exam.cutoff && (
              <section id="cutoff" className="scroll-mt-24">
                <Card className="shadow-lg border-l-4 border-l-orange-600">
                  <CardHeader className="bg-gradient-to-r from-orange-50 to-white">
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      <div className="p-2 bg-orange-600 rounded-lg">
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                      {exam.name} Cut-off Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="grid md:grid-cols-3 gap-6">
                      {exam.cutoff.phase1 && (
                        <CutoffTrendChart data={exam.cutoff.phase1} title="Phase 1 Cut-off" />
                      )}
                      {exam.cutoff.phase2 && (
                        <CutoffTrendChart data={exam.cutoff.phase2} title="Phase 2 Cut-off" />
                      )}
                      {exam.cutoff.final && (
                        <CutoffTrendChart data={exam.cutoff.final} title="Final Cut-off" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Syllabus - Enhanced */}
            {exam.syllabus && (
              <section id="syllabus" className="scroll-mt-24">
                <Card className="shadow-lg border-l-4 border-l-teal-600">
                  <CardHeader className="bg-gradient-to-r from-teal-50 to-white">
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      <div className="p-2 bg-teal-600 rounded-lg">
                        <BookOpen className="w-6 h-6 text-white" />
                      </div>
                      {exam.name} Syllabus 2026
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <Tabs defaultValue="phase1" className="w-full">
                      <TabsList className="grid w-full grid-cols-2 mb-6 bg-gray-100 p-1 rounded-lg">
                        {exam.syllabus.phase1 && (
                          <TabsTrigger value="phase1" className="data-[state=active]:bg-white data-[state=active]:shadow-md rounded-md">
                            Phase 1 / Prelims
                          </TabsTrigger>
                        )}
                        {exam.syllabus.phase2 && (
                          <TabsTrigger value="phase2" className="data-[state=active]:bg-white data-[state=active]:shadow-md rounded-md">
                            Phase 2 / Mains
                          </TabsTrigger>
                        )}
                      </TabsList>
                      
                      {exam.syllabus.phase1 && (
                        <TabsContent value="phase1" className="space-y-4">
                          {exam.syllabus.phase1.map((subject, idx) => (
                            <div key={idx} className="border-2 border-gray-200 rounded-xl p-6 hover:border-teal-300 transition-colors bg-gradient-to-br from-white to-gray-50">
                              <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                                <span className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm">{idx + 1}</span>
                                {subject.subject}
                              </h3>
                              <div className="grid md:grid-cols-2 gap-3">
                                {subject.topics.map((topic, topicIdx) => (
                                  <div key={topicIdx} className="flex items-start gap-2 bg-white p-3 rounded-lg shadow-sm">
                                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-700 text-sm">{topic}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </TabsContent>
                      )}
                      
                      {exam.syllabus.phase2 && (
                        <TabsContent value="phase2" className="space-y-4">
                          {exam.syllabus.phase2.map((paper, idx) => (
                            <div key={idx} className="border-2 border-gray-200 rounded-xl p-6 hover:border-teal-300 transition-colors bg-gradient-to-br from-white to-gray-50">
                              <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                                <span className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm">{idx + 1}</span>
                                {paper.paper || paper.subject}
                              </h3>
                              <div className="grid md:grid-cols-2 gap-3">
                                {paper.topics.map((topic, topicIdx) => (
                                  <div key={topicIdx} className="flex items-start gap-2 bg-white p-3 rounded-lg shadow-sm">
                                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-700 text-sm">{topic}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </TabsContent>
                      )}
                    </Tabs>
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Salary - Enhanced */}
            {exam.salary && (
              <section id="salary" className="scroll-mt-24">
                <Card className="shadow-lg border-l-4 border-l-emerald-600">
                  <CardHeader className="bg-gradient-to-r from-emerald-50 to-white">
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      <div className="p-2 bg-emerald-600 rounded-lg">
                        <DollarSign className="w-6 h-6 text-white" />
                      </div>
                      {exam.name} Salary 2026
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-6">
                    {exam.salary.totalCTC && (
                      <div className="bg-gradient-to-br from-green-100 via-emerald-100 to-teal-100 rounded-2xl p-8 text-center border-4 border-green-300 shadow-lg">
                        <p className="text-gray-700 mb-2 text-lg">Total Annual Package</p>
                        <p className="text-5xl font-bold bg-gradient-to-r from-green-700 to-teal-700 bg-clip-text text-transparent">
                          {exam.salary.totalCTC}
                        </p>
                      </div>
                    )}

                    {exam.salary.range && !exam.salary.totalCTC && (
                      <div className="bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl p-8 text-center border-4 border-blue-300">
                        <p className="text-gray-700 mb-2 text-lg">Salary Range</p>
                        <p className="text-4xl font-bold text-blue-700">{exam.salary.range}</p>
                      </div>
                    )}

                    <div className="grid md:grid-cols-3 gap-4">
                      {exam.salary.basic && (
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border-2 border-purple-200 text-center">
                          <p className="text-sm text-gray-600 mb-2">Basic Pay</p>
                          <p className="text-2xl font-bold text-purple-700">{exam.salary.basic}</p>
                        </div>
                      )}
                      {exam.salary.grossAnnual && (
                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-5 border-2 border-blue-200 text-center">
                          <p className="text-sm text-gray-600 mb-2">Gross Annual</p>
                          <p className="text-2xl font-bold text-blue-700">{exam.salary.grossAnnual}</p>
                        </div>
                      )}
                      {exam.salary.allowances && (
                        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-5 border-2 border-orange-200 text-center">
                          <p className="text-sm text-gray-600 mb-2">Allowances</p>
                          <p className="text-2xl font-bold text-orange-700">{exam.salary.allowances}</p>
                        </div>
                      )}
                    </div>

                    {exam.salary.components && (
                      <div className="bg-gray-50 rounded-xl p-6">
                        <h3 className="font-bold text-lg text-gray-800 mb-4">Salary Components</h3>
                        <div className="grid md:grid-cols-2 gap-3">
                          {exam.salary.components.map((component, idx) => (
                            <div key={idx} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                              <span className="text-gray-700">{component}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Career Growth */}
            {exam.careerGrowth && (
              <section id="career-growth" className="scroll-mt-24">
                <Card className="shadow-lg border-l-4 border-l-pink-600">
                  <CardHeader className="bg-gradient-to-r from-pink-50 to-white">
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      <div className="p-2 bg-pink-600 rounded-lg">
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                      Career Growth & Promotions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="relative">
                      {exam.careerGrowth.map((stage, idx) => (
                        <div key={idx} className="flex items-start gap-4 pb-8 relative">
                          <div className="flex flex-col items-center">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-600 to-orange-600 text-white flex items-center justify-center font-bold text-lg shadow-lg">
                              {idx + 1}
                            </div>
                            {idx < exam.careerGrowth.length - 1 && (
                              <div className="w-0.5 h-full bg-gradient-to-b from-red-400 to-orange-400 absolute top-12"></div>
                            )}
                          </div>
                          <div className="flex-1 bg-gradient-to-r from-white to-gray-50 rounded-xl p-5 border-2 border-gray-200 hover:border-red-300 transition-colors">
                            <h3 className="font-bold text-gray-800 text-lg mb-1">{stage.position}</h3>
                            <p className="text-sm text-gray-600 flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {stage.duration}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Preparation Tips - Enhanced */}
            {exam.preparationTips && (
              <section id="preparation" className="scroll-mt-24">
                <Card className="shadow-lg border-l-4 border-l-blue-600">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-white">
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      <div className="p-2 bg-blue-600 rounded-lg">
                        <Crosshair className="w-6 h-6 text-white" />
                      </div>
                      Preparation Strategy & Tips
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {exam.preparationTips.map((tip, idx) => (
                        <div key={idx} className="flex items-start gap-4 bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl border-l-4 border-blue-500 hover:shadow-md transition-shadow">
                          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                            {idx + 1}
                          </div>
                          <p className="text-gray-700 leading-relaxed flex-1">{tip}</p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl p-8">
                      <h3 className="font-bold text-2xl mb-3">Join Vidya365 Test Series</h3>
                      <p className="mb-6 opacity-90">Get comprehensive mock tests, study material, and expert guidance for {exam.name}</p>
                      <Link to="/student/register">
                        <Button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
                          Register Now <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}

            {/* FAQs Section */}
            <section id="faqs" className="scroll-mt-24">
              <Card className="shadow-lg border-l-4 border-l-yellow-600">
                <CardHeader className="bg-gradient-to-r from-yellow-50 to-white">
                  <CardTitle className="flex items-center gap-3 text-2xl">
                    <div className="p-2 bg-yellow-600 rounded-lg">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    Frequently Asked Questions
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    {faqs.map((faq, idx) => (
                      <FAQItem key={idx} question={faq.question} answer={faq.answer} index={idx} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Final CTA */}
            <div className="bg-gradient-to-br from-red-600 via-red-700 to-orange-700 text-white rounded-2xl p-10 text-center shadow-2xl">
              <h2 className="text-4xl font-bold mb-4">Ready to Start Your {exam.name} Preparation?</h2>
              <p className="text-xl mb-8 opacity-90">Join Vidya365 for comprehensive test series, study materials, and expert guidance</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/student/register">
                  <Button className="bg-white text-red-600 hover:bg-gray-100 px-10 py-6 text-lg font-bold shadow-lg">
                    Register Now
                  </Button>
                </Link>
                <Link to="/courses">
                  <Button variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg font-bold">
                    View Courses
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ExamDetails;
